import React from 'react';
import { connect } from 'react-redux';
import CommonKitComponents from 'components/common/KitComponents/KitComponents.container';
import { actions as uiActions } from 'ducks/ui';
import { bindActionCreators, Dispatch } from 'redux';
import IKitComponentsProperties, { OmittedApiTypes } from 'components/common/KitComponents/KitComponents.properties';
import {
  useCreateComponentLine,
  useCalculateComponentFastLineDetails,
  useRetrieveComponentLines,
  useDeleteComponentLine,
  useUpdateComponentGridLineDetails,
  useCalculateComponentGridLineDetails,
  useGetCreditNoteLineSummary,
  useTotalComponentLinesCount
} from 'api/creditNotes/creditNotes';
import { ICalculateComponentDetailsFacade, IWIPComponentFacade } from 'api/swaggerTypes';

export const FieldName = {
  quantity: 'RequiredQuantity',
  product: 'ProductId',
};

const KitComponents = (props: Readonly<Omit<
  IKitComponentsProperties,
  'classes' | OmittedApiTypes>>) => {

  const params = new URLSearchParams(location.search);
  const WorkCreditNoteLineId = Number(params.get('WorkCreditNoteLineId') ?? '');
  const { mutateAsync: createComponentLineMutateAsync } = useCreateComponentLine();
  const { mutateAsync: updateComponentGridLineMutateAsync } = useUpdateComponentGridLineDetails();
  const { mutateAsync: deleteComponentMutateAsync } = useDeleteComponentLine();
  const { mutateAsync: calculateComponentGridLineDetailsMutateAsync } = useCalculateComponentGridLineDetails();
  const { mutateAsync: calculateComponentFastLineDetailsMutateAsync } = useCalculateComponentFastLineDetails();
  const { mutateAsync: fetchComponentLinesMutateAsync } = useRetrieveComponentLines();
  const { mutateAsync: totalComponentLinesCountMutateAsync } = useTotalComponentLinesCount();
  const { mutateAsync: fetchLineKitSummaryMutateAsync } = useGetCreditNoteLineSummary();

  const fetchComponentLines = React.useCallback(
    (_w, BatchPage, BatchSize) => {
      if (!WorkCreditNoteLineId) {
        return null;
      }

      return fetchComponentLinesMutateAsync({
        WorkCreditNoteLineId,
        urlQueryParams: {
          BatchPage: BatchPage?.toString(),
          BatchSize: BatchSize?.toString()
        }
      });
    },
    [WorkCreditNoteLineId]
  );

  const totalComponentLinesCount = React.useCallback(
    (_w: number) => {
      if (!WorkCreditNoteLineId) {
        return null;
      }

      return totalComponentLinesCountMutateAsync({
        WorkCreditNoteLineId,
        urlQueryParams: {
          BatchPage: 1,
          BatchSize: 1
        }
      });
    },
    [WorkCreditNoteLineId]
  );

  const calculateFastline = React.useCallback(
    (_w, ProductId, Quantity, data) => {
      if (!WorkCreditNoteLineId) {
        return null;
      }

      return calculateComponentFastLineDetailsMutateAsync({
        WorkCreditNoteLineId,
        ProductId: ProductId || 0,
        RequiredQuantity: Quantity || 0,
        urlQueryParams: {
          ChangedField: FieldName[data?.field]
        }
      });
    },
    [WorkCreditNoteLineId]
  );

  const kitSummary = React.useCallback(
    async (_w: number) => {
      if (!WorkCreditNoteLineId) {
        return null;
      }

      return fetchLineKitSummaryMutateAsync({ WorkCreditNoteLineId });
    },
    [WorkCreditNoteLineId]
  );

  const addComponent = React.useCallback(
    (_w, ProductId: number, RequiredQuantity: number) => {
      if (!WorkCreditNoteLineId) {
        return null;
      }

      return createComponentLineMutateAsync({
        WorkCreditNoteLineId,
        ProductId,
        RequiredQuantity
      });
    },
    [WorkCreditNoteLineId]
  );

  const calculateComponentGridLineDetails = React.useCallback(
    (_w: number, ComponentId: number, line: ICalculateComponentDetailsFacade, changedField: keyof (ICalculateComponentDetailsFacade)) => {
      if (!ComponentId) {
        return null;
      }

      return calculateComponentGridLineDetailsMutateAsync({
        ...line,
        WorkCreditNoteLineId,
        ComponentId: ComponentId,
        urlQueryParams: {
          ChangedField: changedField
        }
      });
    },
    []
  );

  const updateComponentGridLineDetails = React.useCallback(
    (_w: number, ComponentId: number, line: IWIPComponentFacade) => {
      if (!ComponentId) {
        return null;
      }

      return updateComponentGridLineMutateAsync({
        ...line,
        WorkCreditNoteLineId,
        ComponentId: ComponentId
      });
    },
    []
  );

  const deleteLine = React.useCallback(
    (componentId: number) => {
      if (!componentId) {
        return null;
      }

      return deleteComponentMutateAsync({
        ComponentId: componentId
      });
    },
    []
  );

  return (<CommonKitComponents
    onFetchComponentLines={fetchComponentLines}
    onCreateComponentLine={addComponent}
    onCalculateComponentFastLineDetails={calculateFastline}
    onCalculateComponentGridLineDetails={calculateComponentGridLineDetails}
    onUpdateComponentGridLine={updateComponentGridLineDetails}
    onDeleteComponent={deleteLine}
    onFetchTotalComponentLinesCount={totalComponentLinesCount}
    getLineKitSummary={kitSummary}
    {...props} />);
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    changeConfirmationDialog: uiActions.changeConfirmationDialog,
    changeValidationDialog: uiActions.changeValidationDialog,
  },
  dispatch);

export default connect(null, mapDispatchToProps)(KitComponents);
